/* eslint-disable @typescript-eslint/no-unused-vars */
import { TimeUtils } from '@/core/event_logic/utils/time_utils';
import { Logger } from '@/shared/logger/logger';

export type ICustomerDataFields = {
  cc?: string;
  ct?: string;
  dob?: string;
  ds: string; // data source
  em?: string;
  fa?: string; // full address
  fn?: string;
  ge?: string;
  id?: string;
  ln?: string;
  ph?: string;
  rc?: string;
  rg?: string;
  street?: string;
  ts?: number;
  zip?: string;
};

export class CustomerDataFields implements ICustomerDataFields {
  ts: number; // timestamp of creation
  fa?: string; // full address
  ds: string; // data source
  id?: string; // Customer ID -> linking
  fn?: string; // First name
  ln?: string;
  em?: string;
  ph?: string;
  dob?: string;
  ge?: string;
  street?: string;
  ct?: string;
  cc?: string;
  rg?: string;
  rc?: string;
  zip?: string;

  static create(
    data: Partial<ICustomerDataFields>,
    timestamp?: number,
  ): CustomerDataFields {
    const instance = new CustomerDataFields();

    instance.init(data, timestamp || TimeUtils.getCurrentEpochInSeconds());
    return instance;
  }

  private constructor() {
    this.ts = 0;
    this.ds = 'unknown';
  }

  private init(data: Partial<ICustomerDataFields>, timestamp?: number): void {
    this.ts = timestamp || data.ts || TimeUtils.getCurrentEpochInSeconds();
    this.ds = data.ds || 'unknown';

    if (!data.ds) {
      Logger.error('Data source is required');
    }

    this.setFa(data.fa);
    this.setId(data.id);
    this.setFn(data.fn);
    this.setLn(data.ln);
    this.setEm(data.em);
    this.setPh(data.ph, data.cc);
    this.setDob(data.dob);
    this.setGe(data.ge);
    this.setStreet(data.street);
    this.setCt(data.ct);
    this.setCc(data.cc);
    this.setRg(data.rg);
    this.setRc(data.rc);
    this.setZip(data.zip);

    this.ds = this.ds || 'unknown';
    this.street = this.street || '';
    this.ct = this.ct || '';
    this.cc = this.cc || '';
    this.rg = this.rg || '';
    this.rc = this.rc || '';

    this.fa = this.fa || '';
    this.id = this.id || '';
    this.fn = this.fn || '';
    this.ln = this.ln || '';
    this.em = this.em || '';
    this.ph = this.ph || '';
    this.dob = this.dob || '';
    this.ge = this.ge || '';

    if (
      !this.fa &&
      !this.id &&
      !this.fn &&
      !this.ln &&
      !this.em &&
      !this.ph &&
      !this.dob &&
      !this.ge &&
      !this.street &&
      !this.ct &&
      !this.cc &&
      !this.rg &&
      !this.rc &&
      !this.zip
    ) {
      this.ds = 'empty';
    }
  }

  private setFa(value?: unknown) {
    if (value && typeof value !== 'string') {
      Logger.warn('Full address must be a string');
    }
    this.fa = (value as string) || '';
  }

  private setId(value?: unknown) {
    if (value && typeof value !== 'string') {
      Logger.warn('ID must be a string');
    }
    this.id = (value as string) || '';
  }

  private setFn(value?: unknown) {
    if (value && typeof value !== 'string') {
      Logger.warn('First name must be a string');
    }
    this.fn = (value as string) || '';
  }

  private setLn(value?: unknown) {
    if (value && typeof value !== 'string') {
      Logger.warn('Last name must be a string');
    }
    this.ln = (value as string) || '';
  }

  private setEm(value?: unknown) {
    if (value && !/^\S[^\s@]*@\S[^\s.]*\.\S+$/.test(value as string)) {
      Logger.error('Invalid email format');
    } else {
      this.em = (value as string) || ''; // this.normalizeEmail(value as string);
    }
  }

  private setPh(value?: unknown, _cc?: string) {
    const phoneRegex = /^(\+|00|0)?[1-9]\d{1,14}$/;
    if (value && !phoneRegex.test(value as string)) {
      Logger.error('Invalid phone number format', value);
      this.ph = (value as string) || '';
    } else {
      this.ph = (value as string) || ''; // this.normalizePhoneNumber(value as string, cc);
    }
  }

  private setDob(value?: unknown) {
    if (value && !/^\d{4}-\d{2}-\d{2}$/.test(value as string)) {
      Logger.error('Date of birth must be in YYYY-MM-DD format');
    }
    this.dob = (value as string) || '';
  }

  private setGe(value?: unknown) {
    if (value && !['female', 'male', 'other'].includes(value as string)) {
      Logger.error('Gender must be either "male", "female" or "other"');
    }
    this.ge = (value as string) || '';
  }

  private setStreet(value?: unknown) {
    if (value && typeof value !== 'string') {
      Logger.warn('Street must be a string');
    }
    this.street = (value as string) || '';
  }

  private setCt(value?: unknown) {
    if (value && typeof value !== 'string') {
      Logger.warn('City must be a string');
    }
    this.ct = (value as string) || '';
  }

  private setCc(value?: unknown) {
    if (value && typeof value !== 'string') {
      Logger.warn('Country code must be a string');
    }
    this.cc = (value as string) || '';
  }

  private setRg(value?: unknown) {
    if (value && typeof value !== 'string') {
      Logger.warn('Region must be a string');
    }
    this.rg = (value as string) || '';
  }

  private setRc(value?: unknown) {
    if (value && typeof value !== 'string') {
      Logger.warn('Region code must be a string');
    }
    this.rc = (value as string) || '';
  }

  private setZip(value?: unknown) {
    try {
      if (value) {
        this.zip = String(value);
      } else {
        Logger.warn('Zip code must be a string');
        this.zip = '';
      }
    } catch (error) {
      Logger.error('Failed to set zip code', error);
      this.zip = '';
    }
  }

  equals(other: CustomerDataFields): boolean {
    return (
      this.fa === other.fa &&
      this.ds === other.ds &&
      this.id === other.id &&
      this.fn === other.fn &&
      this.ln === other.ln &&
      this.em === other.em &&
      this.ph === other.ph &&
      this.dob === other.dob &&
      this.ge === other.ge &&
      this.street === other.street &&
      this.ct === other.ct &&
      // this.cc === other.cc &&
      this.rg === other.rg &&
      this.rc === other.rc &&
      this.zip === other.zip //
    );
  }
}

export type TCustomerDataArrays = {
  cc: string[]; // country code
  ct: string[]; // city
  dob: string[]; // date of birth
  ds: string[]; // data source
  em: string[]; // normalizedEmail
  fn: string[]; // first name
  ge: string[]; // gender
  id: string[]; // customer id
  ln: string[]; // last name
  ph: string[]; // phone number
  rc: string[]; // region code
  rg: string[]; // region
  street: string[]; // street
  zip: string[]; // zip code
};
